import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PhotoGallery } from '@/components/PhotoGallery/PhotoGallery';
import { ReturnOrderContainerSource } from '@/graphql/generatedTypes';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { Routes } from '@/utils/routes';

import { ItemTypeCell } from './DetailFormParts';
import type { ItemFormState, ItemKind, OpsOrderDetail } from './types';

type ReturnIntake = NonNullable<OpsOrderDetail['return_intake']>;

interface IntakeRow {
  key: string;
  name: string;
  imageUrl: string | null;
  hexCode: string | null;
  announced: number;
  received: number | null;
  breakdown?: { identified: number; counted: number };
  broken?: { byWarehouse: number | null; byPartner: number };
}

interface InboundIntakeSectionProps {
  intake: ReturnIntake;
  items: ItemFormState[];
}

export function InboundIntakeSection({
  intake,
  items,
}: InboundIntakeSectionProps) {
  const rows = buildRows(intake, items);
  const status = intakeStatus(intake);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{i18n.t('Order:sectionIntake')}</CardTitle>
        {status.description && (
          <CardDescription>{status.description}</CardDescription>
        )}
        <CardAction>
          <Badge variant={status.variant}>{status.label}</Badge>
        </CardAction>
      </CardHeader>
      <CardContent className="tw:flex tw:flex-col tw:gap-6">
        {rows.length === 0 ? (
          <p className="tw:text-muted-foreground">
            {i18n.t('Order:tableNoItems')}
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{i18n.t('Order:tableItemType')}</TableHead>
                <TableHead className="tw:text-right">
                  {i18n.t('Order:tableAnnounced')}
                </TableHead>
                <TableHead className="tw:text-right">
                  {i18n.t('Order:tableReceived')}
                </TableHead>
                <TableHead className="tw:text-right">
                  {i18n.t('Order:tableBroken')}
                </TableHead>
                <TableHead className="tw:text-right">
                  {i18n.t('Order:tableDifference')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.key}>
                  <TableCell>
                    <ItemTypeCell
                      name={row.name}
                      imageUrl={row.imageUrl}
                      hexCode={row.hexCode}
                      caption={
                        row.breakdown && row.announced > 0
                          ? i18n.t(
                              'Order:announcedContainerBreakdown',
                              row.breakdown,
                            )
                          : undefined
                      }
                    />
                  </TableCell>
                  <TableCell className="tw:text-right tw:tabular-nums">
                    {row.announced}
                  </TableCell>
                  <TableCell className="tw:text-right tw:tabular-nums">
                    {row.received ?? (
                      <span className="tw:text-muted-foreground">–</span>
                    )}
                  </TableCell>
                  <TableCell className="tw:text-right tw:tabular-nums">
                    <Broken row={row} />
                  </TableCell>
                  <TableCell className="tw:text-right tw:tabular-nums">
                    <Difference row={row} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {intake.containers
          .filter((line) => line.scan_required)
          .map((line) => (
            <IntakeScans
              key={line.container_type_id}
              line={line}
              completed={intake.completed_at != null}
            />
          ))}

        {intake.partner_photos.length > 0 && (
          <div className="tw:flex tw:flex-col tw:gap-2">
            <p className="tw:font-medium">
              {i18n.t('Order:partnerPhotos', {
                count: intake.partner_photos.length,
              })}
            </p>
            <PhotoGallery photos={intake.partner_photos} />
          </div>
        )}

        {intake.photos.length > 0 && (
          <div className="tw:flex tw:flex-col tw:gap-2">
            <p className="tw:font-medium">
              {i18n.t('Order:intakePhotos', { count: intake.photos.length })}
            </p>
            <PhotoGallery photos={intake.photos} />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function buildRows(intake: ReturnIntake, items: ItemFormState[]): IntakeRow[] {
  const byKind = (kind: ItemKind) =>
    new Map(
      items
        .filter((item) => item.kind === kind)
        .map((item) => [item.type_id, item]),
    );
  const containers = byKind('container');
  const storeAssets = byKind('store_asset');
  const untracked = byKind('untracked');
  return [
    ...intake.containers.map((line) => {
      const item = containers.get(line.container_type_id);
      return {
        key: `container-${line.container_type_id}`,
        name: line.name ?? item?.typeName ?? `#${line.container_type_id}`,
        imageUrl: line.image_url ?? item?.imageUrl ?? null,
        hexCode: item?.hexCode ?? null,
        announced: line.announced,
        received: line.received ?? null,
        breakdown: { identified: line.identified, counted: line.counted },
        broken: {
          byWarehouse: line.broken ?? null,
          byPartner: line.reported_broken,
        },
      };
    }),
    ...intake.store_assets.map((line) => {
      const item = storeAssets.get(line.item_type_id);
      return {
        key: `store-asset-${line.item_type_id}`,
        name: line.name ?? item?.typeName ?? `#${line.item_type_id}`,
        imageUrl: line.image_url ?? item?.imageUrl ?? null,
        hexCode: item?.hexCode ?? null,
        announced: line.announced,
        received: line.received ?? null,
      };
    }),
    ...intake.untracked_items.map((line) => ({
      key: `untracked-${line.type_id}`,
      name:
        line.name ??
        untracked.get(line.type_id)?.typeName ??
        `#${line.type_id}`,
      imageUrl: null,
      hexCode: null,
      announced: line.announced,
      received: line.received ?? null,
    })),
  ];
}

type IntakeContainerLine = ReturnIntake['containers'][number];

function containerLabel(container: {
  id: string;
  name?: string | null;
  short_id?: string | null;
}) {
  return container.name ?? container.short_id ?? container.id.slice(0, 8);
}

function IntakeScans({
  line,
  completed,
}: {
  line: IntakeContainerLine;
  completed: boolean;
}) {
  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      <div className="tw:flex tw:flex-col">
        <p className="tw:font-medium">
          {line.name ?? `#${line.container_type_id}`}
        </p>
        <p className="tw:text-muted-foreground">
          {i18n.t('Order:intakeScansDetail', {
            scanned: line.scanned_containers.length,
            received: line.received ?? 0,
          })}
        </p>
      </div>
      {line.scanned_containers.length > 0 && (
        <div className="tw:flex tw:flex-wrap tw:gap-1.5">
          {line.scanned_containers.map((container) => (
            <Badge
              key={container.id}
              variant={container.announced ? 'outline' : 'warning'}
              asChild
            >
              <Link to={Routes.container(container.id)}>
                {containerLabel(container)}
                {container.source === ReturnOrderContainerSource.Typed &&
                  ` · ${i18n.t('Order:intakeScanTyped')}`}
                {!container.announced &&
                  ` · ${i18n.t('Order:intakeScanNotAnnounced')}`}
              </Link>
            </Badge>
          ))}
        </div>
      )}
      {line.missing_containers.length > 0 && (
        <>
          <p className="tw:text-muted-foreground tw:mt-1">
            {i18n.t(
              completed
                ? 'Order:intakeMissingSentBack'
                : 'Order:intakeMissingNotScanned',
            )}
          </p>
          <div className="tw:flex tw:flex-wrap tw:gap-1.5">
            {line.missing_containers.map((container) => (
              <Badge key={container.id} variant="destructive" asChild>
                <Link to={Routes.container(container.id)}>
                  {containerLabel(container)}
                </Link>
              </Badge>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(
    i18n.language,
    getDateFormatOptions(true),
  );
}

function intakeStatus(intake: ReturnIntake): {
  label: string;
  variant: 'success' | 'subtle';
  description?: string;
} {
  if (intake.completed_at) {
    return {
      label: i18n.t('Order:intakeStatusCompleted'),
      variant: 'success',
      description: i18n.t('Order:intakeCompletedAt', {
        date: formatDate(intake.completed_at),
      }),
    };
  }
  return { label: i18n.t('Order:intakeStatusNotStarted'), variant: 'subtle' };
}

function Broken({ row }: { row: IntakeRow }) {
  if (!row.broken) return <span className="tw:text-muted-foreground">–</span>;
  const { byWarehouse, byPartner } = row.broken;
  return (
    <div className="tw:flex tw:flex-col tw:items-end">
      {byWarehouse === null ? (
        <span className="tw:text-muted-foreground">–</span>
      ) : byWarehouse !== byPartner ? (
        <Badge variant="warning" className="tw:tabular-nums">
          {byWarehouse}
        </Badge>
      ) : (
        <span className={byWarehouse === 0 ? 'tw:text-muted-foreground' : ''}>
          {byWarehouse}
        </span>
      )}
      {byPartner > 0 && (
        <span className="tw:text-muted-foreground tw:text-xs">
          {i18n.t('Order:reportedBrokenByPartner', { count: byPartner })}
        </span>
      )}
    </div>
  );
}

function Difference({ row }: { row: IntakeRow }) {
  if (row.received === null) {
    return <span className="tw:text-muted-foreground">–</span>;
  }
  const difference = row.received - row.announced;
  if (difference === 0) {
    return <span className="tw:text-muted-foreground">0</span>;
  }
  return (
    <Badge
      variant={difference < 0 ? 'destructive' : 'warning'}
      className="tw:tabular-nums"
    >
      {difference > 0 ? `+${difference}` : `−${Math.abs(difference)}`}
    </Badge>
  );
}
